/**
 * Anzeigeregeln der K&N-Sendungsansicht — was ein FEHLENDER Wert zeigen darf.
 *
 * 🔴 **Der Grund, warum es diese Datei gibt.** Bei Kuehne+Nagel ist ein
 * fehlender Wert der NORMALFALL, nicht ein Defekt: `eta_at` (die Zustellung am
 * Ziel) ist heute bei allen drei laufenden Sendungen leer, weil K&N kein
 * Zustelldatum liefert und in den bestellten Leistungen kein `del` steht.
 * Masse, Schiff und Reise fehlen je nach Verkehrstraeger.
 *
 * ⚠️ Hier stand „es ist keine Zustellung beauftragt". Das ist die FOLGE, nicht
 * der Stand — und sie ist nicht gedeckt, solange vier Leistungscodes in keiner
 * Liste der K&N-Doku stehen. Es war der SIEBTE Fundort derselben Formulierung
 * in dieser Bahn und der einzige, den eine Suche ueber die Oberflaeche nicht
 * gefunden haette: er steht im Kommentar, nicht im JSX. Gefunden vom
 * Regel-16-Tor in seiner Wiedervorlage.
 *
 * Daraus folgen zwei Regeln, und beide sind Zusicherungen, keine Kosmetik:
 *
 *   1. **Ein fehlender Wert erscheint NIE als `0`.** `0 kg` ist eine Aussage
 *      ueber die Sendung; `—` ist die Aussage, dass K&N nichts gesagt hat.
 *   2. **Ein fehlendes Datum erscheint NIE als heute.** Es gibt hier keinen
 *      `new Date()`-Rueckfall, nirgends. Wer einen einbaut, erfindet einen
 *      Termin — und die Ansicht kann „nicht geliefert" danach nicht mehr von
 *      „heute" unterscheiden.
 *
 * Die Zeitformatierung kommt aus `utils/time.js` und wird hier NICHT
 * nachgebaut: `formatBerlinDate`/`-DateTime` tragen bereits denselben
 * Platzhalter und dieselbe Zeitzone wie der Rest der Anwendung.
 */

import { formatUnit } from './format';

/** Der Platzhalter fuer „K&N hat dazu nichts geliefert". */
export const STRICH = '—';

/**
 * Ein Wert oder der Strich — mit einer Einheit, wenn der Wert da ist.
 *
 * 🔴 `0` und `false` sind WERTE und kommen durch. Nur `null`/`undefined`/`''`
 * werden zum Strich. Ein `== null`-Vergleich statt eines
 * Wahrheitswert-Vergleichs ist hier die ganze Zusicherung: `if (!wert)` haette
 * aus einem gemessenen Nullgewicht einen fehlenden Wert gemacht.
 */
export function wertOderStrich(wert, einheit = '') {
  if (wert === null || wert === undefined || wert === '') return STRICH;
  return einheit ? `${wert} ${einheit}` : String(wert);
}

/** Gramm als Kilogramm, mit einer Nachkommastelle. `null` bleibt der Strich. */
export function gewichtText(gramm) {
  if (gramm === null || gramm === undefined) return STRICH;
  return formatUnit(gramm / 1000, 'kg', { digits: 1 });
}

/** Herkunft → Ziel, mit Code UND Name, soweit beides da ist. */
export function ortText(code, name) {
  if (!code && !name) return STRICH;
  if (code && name) return `${name} (${code})`;
  return code || name;
}

/**
 * Wie eine Sendung heute heisst, wenn ihr Zustand unbekannt ist.
 *
 * `current_stage` kommt roh von K&N (`IN_TRANSIT`, `BOOKED`, …). Unbekannte
 * Werte werden **gezeigt**, nicht umgedeutet — der Abzug meldet sie ohnehin
 * als Befund, und eine Ansicht, die sie verschluckt, macht ihn unsichtbar.
 */
export function stufeText(stage) {
  if (!stage) return STRICH;
  return String(stage).replaceAll('_', ' ').toLowerCase();
}

/**
 * Kubikmeter mit zwei Nachkommastellen. `null` bleibt der Strich.
 *
 * 🔴 Zwei Stellen, nicht eine: die Spalte ist `NUMERIC(10,3)` (Migration 287),
 * und die gemessenen Sendungen liegen im einstelligen Kubikmeterbereich. Eine
 * Stelle machte aus 1,44 und 1,35 dieselbe Zahl.
 */
export function volumenText(cbm) {
  if (cbm === null || cbm === undefined) return STRICH;
  return formatUnit(cbm, 'm³');
}

/**
 * Menge mit ihrer Einheit, so wie K&N sie nennt.
 *
 * 🔴 Die Einheit wird NICHT uebersetzt und NICHT geraten. K&N liefert `PLT`,
 * `PCE`, `CT` und weitere; eine Abbildungstabelle waere eine Verbotsliste mit
 * einer naechsten Form, und ein unbekannter Code erschiene darin als leer.
 * Fehlt die Einheit, steht die nackte Zahl — das ist ehrlicher als ein
 * erfundenes „Stk.".
 */
export function mengeText(menge, einheit) {
  if (menge === null || menge === undefined) return STRICH;
  return einheit ? `${menge} ${einheit}` : String(menge);
}

/**
 * Die K&N-Checklistenpunkte auf Englisch — die Servertexte sind deutsch.
 *
 * 🔴 **Warum die Abbildung HIER steht und nicht im Server.** Die drei Texte
 * sind `CHECKLISTE_*` in `backend/logistics/pol_pod.py` und werden dort auch
 * fuer die Vorschau der Beauftragungsmail gebraucht — sie sind Fachsprache der
 * Liste, kein Sichttext der Bestellseite. Uebersetzt wird deshalb an der
 * Anzeige, mit derselben Trennung, die `utils/format.js` fuer Zahlen zieht.
 *
 * 🔴 **Ein unbekannter Text faellt auf den Rohtext zurueck, NICHT auf nichts.**
 * Die Wertemenge kann wachsen; eine Luecke, die verschwindet, weil ihre
 * Uebersetzung fehlt, waere schlimmer als eine deutsche — der Bediener
 * erfuehre dann gar nicht, dass etwas fehlt. Dieselbe Regel, die
 * `Sendungsdokumente` fuer einen unbekannten `kind` schon zieht.
 */
const CHECKLISTE_TEXT = {
  'POL / POD': 'Ports (POL / POD)',
  'Sendung stapelbar / nicht stapelbar': 'Stackable',
  'Anzahl Paletten': 'Pallets',
}

/** Ein Checklistenpunkt auf Englisch — oder der Rohtext, wenn er unbekannt ist. */
export function checklistenLueckeText(text) {
  if (text === null || text === undefined || text === '') return STRICH
  return CHECKLISTE_TEXT[text] || String(text)
}

/**
 * Die ganze Lueckenliste als EIN Satz, oder `null`, wenn nichts fehlt.
 *
 * `null` und nicht `''`: die Anzeige haengt daran, ob sie die Zeile ueberhaupt
 * rendert. Ein leerer Satz waere eine Zeile ohne Aussage.
 */
export function checklistenLueckenText(liste) {
  const texte = (Array.isArray(liste) ? liste : [])
    .map(checklistenLueckeText)
    .filter((t) => t && t !== STRICH)
  return texte.length ? `Missing for K&N: ${texte.join(', ')}` : null
}
